import React, { useState } from "react";
import "./../styles/Login.css";
import { useNavigate } from "react-router-dom";
import { gql } from "@apollo/client";
import loginClient from "../apolloLoginClient";
import createAccountClient from "../apolloCreateAccountClient";
import LearnHutLogo from "../Logos To be used/learnhut_logo_recolored.png";
import { persistAuthSession } from "../utils/authSession";

// Use same faculty for all accounts (for now)
const DEFAULT_FACULTY_ID = "1c4e71de-9018-44d3-929e-042704d86516";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const getLoginErrorMessage = (err) => {
  const message = String(err?.message || "");
  const isNetworkOrCorsError =
    Boolean(err?.networkError) ||
    /failed to fetch|network (?:request )?failed|network error|load failed/i.test(message);

  if (isNetworkOrCorsError) {
    return "Verbindung zum Anmeldeserver fehlgeschlagen. Bitte prüfe deine Internetverbindung und verwende https://learnhut.de ohne „www“.";
  }

  return "Falsche E-Mail oder Kennwort!";
};

// GraphQL Mutations
const LOGIN_MUTATION = gql`
  mutation LoginUser($input: LoginInput!) {
    loginUser(input: $input) {
      token
      user {
        id
        email
        name
        universityId
        role
        facultyId
      }
    }
  }
`;

// IMPORTANT: User type does NOT have createdAt/updatedAt in your schema.
const CREATE_ACCOUNT_MUTATION = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      email
      name
      universityId
      role
      facultyId
    }
  }
`;

const Login = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [loginPath, setLoginPath] = useState("student"); // "student" | "tutor"
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [university, setUniversity] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const resetErrors = () => {
    setError(false);
    setErrorMessage("");
  };

  const setLoginMode = (mode) => {
    if (mode !== "student" && mode !== "tutor") return;
    setLoginPath(mode);
    setShowPassword(false);
    if (mode === "tutor") {
      setIsRegistering(false);
    }
    resetErrors();
  };

  // Login Handler
  const handleLogin = async () => {
    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail || !password) {
      setError(true);
      setErrorMessage("E-Mail oder Kennwort fehlt!");
      return;
    }

    setEmail(normalizedEmail);
    setLoading(true);
    resetErrors();

    try {
      const { data } = await loginClient.mutate({
        mutation: LOGIN_MUTATION,
        variables: { input: { email: normalizedEmail, password } },
      });

      const { token, user } = data.loginUser;

      if (!token) throw new Error("No token received");

      // Tutor/Admin path restriction
      if (loginPath === "tutor") {
        const role = user?.role;
        const allowed = role === "TUTOR" || role === "ADMIN";

        if (!allowed) {
          setError(true);
          setErrorMessage(
            "Dieses Login ist nur für Tutor- und Admin-Konten. Bitte ein Student-Konto wählen."
          );
          setLoading(false);
          return;
        }
      }

      persistAuthSession({ token, user });

      setLoading(false);

      // ✅ Routing based on chosen login path
      if (loginPath === "tutor") {
        navigate("/dashboard-tutor", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Login Error:", err.message || err);
      setError(true);
      setErrorMessage(getLoginErrorMessage(err));
      setLoading(false);
    }
  };

  // Register Handler
  const handleRegister = async () => {
    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail || !username || !password) {
      setError(true);
      setErrorMessage("Alle Felder müssen ausgefüllt werden");
      return;
    }
    if (password.length < 8) {
      setError(true);
      setErrorMessage("Kennwort muss mindestens 8 Zeichen haben.");
      return;
    }

    setEmail(normalizedEmail);
    setLoading(true);
    resetErrors();

    try {
      // Role is intentionally NOT sent -> backend defaults to STUDENT
      const { data } = await createAccountClient.mutate({
        mutation: CREATE_ACCOUNT_MUTATION,
        variables: {
          input: {
            email: normalizedEmail,
            name: username,
            password,
            facultyId: DEFAULT_FACULTY_ID, // ✅ fixed value for now
            universityId: university || null, // optional in schema
          },
        },
      });

      console.log("Create Account Response:", data);

      // Auto-login after success
      await handleLogin();
    } catch (err) {
      console.error("Account Creation Error:", err.message || err);
      setError(true);

      const msg =
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        "Fehler beim Erstellen des Kontos";

      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-decor decor-top" aria-hidden="true" />
      <div className="login-decor decor-bottom" aria-hidden="true" />

      <main className="login-main">
        <section className="login-box">
          <header className="login-branding">
            <div className="brand-mark" aria-hidden="true">
              <img src={LearnHutLogo} alt="LearnHut Logo" className="brand-mark-icon" />
            </div>
            <div className="brand-title-row">
              <h1 className="brand-title">LearnHut</h1>
              <span className="brand-beta">BETA</span>
            </div>
            <p className="brand-subtitle">Die Plattform, die dich erfolgreich durch die Prüfung bringt.</p>
          </header>

          {!isRegistering && (
            <div className="auth-switch" role="tablist" aria-label="Kontotyp">
              <button
                type="button"
                role="tab"
                className={`auth-pill ${loginPath === "student" ? "active" : ""}`}
                onClick={() => setLoginMode("student")}
                aria-selected={loginPath === "student"}
              >
                Student
              </button>
              <button
                type="button"
                role="tab"
                className={`auth-pill ${loginPath === "tutor" ? "active" : ""}`}
                onClick={() => setLoginMode("tutor")}
                aria-selected={loginPath === "tutor"}
              >
                Tutor
              </button>
            </div>
          )}

          <div className="form-stack">
            <div className="field-group">
              <label htmlFor="login-email">OVGU E-Mail</label>
              <div className="field-shell">
                <span className="field-icon" aria-hidden="true">@</span>
                <input
                  id="login-email"
                  type="email"
                  placeholder="name@hochschule.de"
                  value={email}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    resetErrors();
                  }}
                  onBlur={() => setEmail(normalizeEmail(email))}
                />
              </div>
            </div>

            {isRegistering && (
              <>
                <div className="field-group">
                  <label htmlFor="register-username">Benutzername</label>
                  <div className="field-shell">
                    <span className="field-icon" aria-hidden="true">👤</span>
                    <input
                      id="register-username"
                      type="text"
                      placeholder="Benutzername*"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        resetErrors();
                      }}
                    />
                  </div>
                </div>

                <div className="field-group">
                  <label htmlFor="register-password">Kennwort</label>
                  <div className="field-shell">
                    <span className="field-icon" aria-hidden="true">🔒</span>
                    <input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Kennwort* (mind. 8 Zeichen)"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        resetErrors();
                      }}
                    />
                    <button
                      type="button"
                      className="field-visibility-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? "Kennwort verbergen" : "Kennwort anzeigen"}
                    >
                      {showPassword ? "Verbergen" : "Anzeigen"}
                    </button>
                  </div>
                </div>

                <div className="field-group">
                  <label htmlFor="register-university">Universität</label>
                  <div className="field-shell select-shell">
                    <select
                      id="register-university"
                      value={university}
                      onChange={(e) => {
                        setUniversity(e.target.value);
                        resetErrors();
                      }}
                    >
                      <option value="">Universität auswählen (optional)</option>
                      <option value="e9b1a9c3-de7b-403a-aeac-c2318b9b49f9">
                        OVGU Magdeburg
                      </option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {!isRegistering && (
              <div className="field-group">
                <div className="field-row-label">
                  <label htmlFor="login-password">Kennwort</label>
                  <p className="forgot-password">Kennwort vergessen?</p>
                </div>
                <div className="field-shell">
                  <span className="field-icon" aria-hidden="true">🔒</span>
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      resetErrors();
                    }}
                  />
                  <button
                    type="button"
                    className="field-visibility-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Kennwort verbergen" : "Kennwort anzeigen"}
                  >
                    {showPassword ? "Verbergen" : "Anzeigen"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {error && <p className="error-message">{errorMessage}</p>}

          {isRegistering ? (
            <>
              <button
                className="login-button"
                onClick={handleRegister}
                disabled={loading}
              >
                <span>{loading ? "Lädt..." : "Konto erstellen"}</span>
                <span className="button-arrow" aria-hidden="true">→</span>
              </button>

              <p
                className="back-to-login"
                onClick={() => {
                  setIsRegistering(false);
                  setShowPassword(false);
                }}
              >
                Zurück zum Login
              </p>
            </>
          ) : (
            <>
              <button
                className="login-button"
                onClick={handleLogin}
                disabled={loading}
              >
                <span>{loading ? "Lädt..." : "Zum Dashboard"}</span>
                <span className="button-arrow" aria-hidden="true">→</span>
              </button>

              <footer className="login-footer">
                {loginPath === "student" && (
                  <p className="signup-message">
                    Du hast noch kein Konto?{" "}
                    <span
                      className="signup-link"
                      onClick={() => {
                        setIsRegistering(true);
                        setShowPassword(false);
                      }}
                    >
                      Registrieren
                    </span>
                  </p>
                )}

                {/* <div className="login-footer-links">
                  <button type="button" className="login-footer-link-btn">
                    Datenschutz
                  </button>
                  <button type="button" className="login-footer-link-btn">
                    Support
                  </button>
                </div> */}
              </footer>
            </>
          )}
        </section>

        <div className="system-pill" aria-hidden="true">
          <span className="system-dot" />
          <span>Systemstatus: Betriebsbereit</span>
        </div>
      </main>
    </div>
  );
};

export default Login;
